import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Camera, Video, ExternalLink, Calendar, Star, MapPin } from 'lucide-react';
import { API } from '../config/api';



export default function VideographerPage() {
  const { videographerId } = useParams();
  const [videographer, setVideographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVideographer();
  }, [videographerId]);

  const fetchVideographer = async () => {
    try {
      const response = await fetch(`${API}/videographers/${videographerId}`);
      if (response.ok) {
        setVideographer(await response.json());
      } else {
        setError('Videographer not found');
      }
    } catch (err) {
      setError('Failed to load videographer');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !videographer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="glass-card p-8 text-center max-w-md">
          <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-heading text-2xl font-bold mb-2">Videographer Not Found</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/creatives">
            <Button>Back to Creatives</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="videographer-page">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="relative mb-8">
          <div className="h-48 sm:h-64 rounded-xl bg-gradient-to-br from-purple-900/50 to-pink-900/50 overflow-hidden">
            {videographer.banner_url && (
              <img src={videographer.banner_url} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 -mt-16 sm:-mt-20 px-4 sm:px-6">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl bg-purple-500/20 border-4 border-background flex items-center justify-center overflow-hidden">
              {videographer.avatar_url ? (
                <img src={videographer.avatar_url} alt={videographer.display_name} className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-16 h-16 text-purple-400" />
              )}
            </div>
            <div className="flex-1 pt-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold" data-testid="videographer-name">
                  {videographer.display_name}
                </h1>
                {videographer.is_verified && (
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Verified</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
                <span className="flex items-center gap-1">
                  <Video className="w-4 h-4" /> {videographer.credits_count || 0} credits
                </span>
                {videographer.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {videographer.location}
                  </span>
                )}
              </div>
              {videographer.booking_link && (
                <a href={videographer.booking_link} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-purple-600 hover:bg-purple-700">
                    <Calendar className="w-4 h-4 mr-2" /> Book Now
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Tabs defaultValue="about">
              <TabsList className="bg-muted/50 mb-6">
                <TabsTrigger value="about">About</TabsTrigger>
                <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
              </TabsList>

              <TabsContent value="about">
                <Card className="glass-card border-border p-6">
                  <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Bio</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {videographer.bio || 'No bio provided yet.'}
                  </p>
                  
                  {videographer.services && videographer.services.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Services</h3>
                      <div className="flex flex-wrap gap-2">
                        {videographer.services.map((service, i) => (
                          <Badge key={i} variant="outline" className="bg-purple-500/10 border-purple-500/30 text-purple-400">
                            {service.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {videographer.equipment && videographer.equipment.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Equipment</h3>
                      <div className="flex flex-wrap gap-2">
                        {videographer.equipment.map((item, i) => (
                          <Badge key={i} variant="outline">{item}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="portfolio">
                <Card className="glass-card border-border p-8 text-center">
                  <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Portfolio coming soon</p>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="space-y-6">
            <Card className="glass-card border-border p-4">
              <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Credits</span>
                  <span className="font-bold">{videographer.credits_count || 0}</span>
                </div>
                {videographer.rate_range && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rate</span>
                    <span className="font-bold text-green-400">{videographer.rate_range}</span>
                  </div>
                )}
              </div>
            </Card>

            {videographer.portfolio_url && (
              <Card className="glass-card border-border p-4">
                <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Portfolio</h3>
                <a href={videographer.portfolio_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full">
                    <ExternalLink className="w-4 h-4 mr-2" /> View Portfolio
                  </Button>
                </a>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
